import { resolveEndEffectorBodyUuid, type SceneEeResolveSlice } from "./endEffectorResolve";
import { getBehaviorTrainedModel } from "./trainedModels";
import type { BehaviorDocument, BTComposite, BTLeaf, BTNodeId } from "./types";

function isNonEmptyString(v: unknown): v is string {
  return typeof v === "string" && v.trim().length > 0;
}

function bodyExists(scene: SceneEeResolveSlice, uuid: string): boolean {
  return isNonEmptyString(uuid) && uuid in (scene.bodies ?? {});
}

function leafNeedsUserInput(
  node: BTLeaf,
  doc: BehaviorDocument,
  scene: SceneEeResolveSlice
): boolean {
  switch (node.kind) {
    case "openGripper":
    case "closeGripper":
    case "markSuccess":
    case "markFailure":
    case "wait":
    case "conditionTimeElapsed":
      return false;

    case "servoPickPlace": {
      if (!isNonEmptyString(node.pickBodyUuid) || !bodyExists(scene, node.pickBodyUuid))
        return true;
      if (
        !isNonEmptyString(node.endEffectorRef) ||
        resolveEndEffectorBodyUuid(scene, node.endEffectorRef) == null
      )
        return true;
      if (isNonEmptyString(node.dropBodyUuid) && !bodyExists(scene, node.dropBodyUuid)) return true;
      return false;
    }
    case "moveEeToXyz":
    case "moveEeByOffset":
      return (
        !isNonEmptyString(node.endEffectorRef) ||
        resolveEndEffectorBodyUuid(scene, node.endEffectorRef) == null
      );
    case "runTrainedModel":
      return getBehaviorTrainedModel(node.modelId) == null;
    case "goToKeyframe":
      return !isNonEmptyString(node.keyframeName);
    case "randomizeObjectPose":
      return !isNonEmptyString(node.bodyUuid) || !bodyExists(scene, node.bodyUuid);
    case "setObjectPose":
      return !isNonEmptyString(node.bodyUuid) || !bodyExists(scene, node.bodyUuid);
    case "resetToBaseline":
      return doc.baselineSnapshot == null;
    case "conditionObjectGrasped":
      return !isNonEmptyString(node.bodyUuid) || !bodyExists(scene, node.bodyUuid);
    case "conditionObjectNear":
      if (!isNonEmptyString(node.bodyUuid) || !bodyExists(scene, node.bodyUuid)) return true;
      return !Number.isFinite(node.toleranceM) || node.toleranceM < 0;
    case "conditionEeNear":
      if (
        !isNonEmptyString(node.endEffectorRef) ||
        resolveEndEffectorBodyUuid(scene, node.endEffectorRef) == null
      )
        return true;
      return !Number.isFinite(node.toleranceM) || node.toleranceM < 0;
    default: {
      const _exhaustive: never = node;
      void _exhaustive;
      return false;
    }
  }
}

function compositeNeedsUserInput(node: BTComposite, doc: BehaviorDocument): boolean {
  if (node.kind === "retry" || node.kind === "repeat") {
    return node.child == null;
  }
  if (node.kind === "ifThenElse") {
    if (node.conditionChild == null || node.thenChild == null) return true;
    const conditionNode = doc.nodes[node.conditionChild];
    if (!conditionNode) return true;
    return !(
      conditionNode.kind === "conditionObjectGrasped" ||
      conditionNode.kind === "conditionEeNear" ||
      conditionNode.kind === "conditionObjectNear" ||
      conditionNode.kind === "conditionTimeElapsed"
    );
  }
  return false;
}

/**
 * Nodes that still need inspector input (or scene linkage) before a run is meaningful.
 * Uses the same kind of checks as leaf ticks (refs, bodies, baseline), without physics.
 */
export function getIncompleteBehaviorNodeIds(
  doc: BehaviorDocument,
  scene: SceneEeResolveSlice
): Set<BTNodeId> {
  const incomplete = new Set<BTNodeId>();
  for (const id of Object.keys(doc.nodes)) {
    const node = doc.nodes[id];
    if (!node) continue;
    if (
      "children" in node ||
      node.kind === "retry" ||
      node.kind === "repeat" ||
      node.kind === "ifThenElse"
    ) {
      if (compositeNeedsUserInput(node as BTComposite, doc)) incomplete.add(id);
      continue;
    }
    if (leafNeedsUserInput(node as BTLeaf, doc, scene)) incomplete.add(id);
  }
  return incomplete;
}
